import { formatListName } from "@/shared/utils/formatListName";
import NewsquawkHeadlineItem from "./NewsquawkHeadlineItem";
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { CommonWidgetProps } from "../types";
import { Headline } from "./models/Headline";
import DeleteConfirmationDialog from "./DeleteConfirmationDialog";
import EditHeadlineFormDialog from "./EditHeadlineFormDialog";
import PollingModeIndicator from "@/shared/components/PollingModeIndicator";
import EmptyPlaceholder from "@/ui-components/empty-placeholder";
import { useAccessControl } from "@/hooks/useAccessControl";
import { usePortfoliosCRUD } from "@/pages/PortfolioBuilder/api/usePortfoliosCRUD";
import { usePortfolioBuilderOptional } from "@/pages/PortfolioBuilder/components/PortfolioBuilderContext";
import { applyFilters } from "./hooks/useHeadlinesHighlightFilterMenu";
import { useHeadlinesFilterPanel } from "./hooks/useHeadlinesFilterPanel";
import { buildHighlightCandidates } from "@/shared/utils/highlightMatches";
import { combinedAlertIds } from "@/shared/utils/alertLists";
import { mergeCombinedFilters } from "@/shared/utils/combinedFilters";
import { useHeadlinesHighlightPanel } from "./hooks/useHeadlinesHighlightPanel";
import FilterHighlightPanels from "@/ui-components/FilterPanel/FilterHighlightPanels";
import useStableArray from "@/hooks/useStableArray";
import { useHeadlineQueriesCRUD } from "./api/headline-query/useHeadlineQueriesCRUD";
import { CategoryQuery } from "./models/CategoryQuery";
import {
  mapPortfolioIdsToPortfolios,
} from "../firehose-widgets/shared/hooks/searchUtils";
import {
  useHeadlines,
  useHeadlinesCRUD,
  useDelayedHeadlines,
  useSearchHeadlines,
  useIsPollingMode,
} from "@/shared/api/newsquawk-headlines";
import { useHeadlinesFilterStore } from "@/shared/api/newsquawk-headlines/HeadlinesFilterStore";
import { trackFilterApplied } from "@/utils/gtm";
import { menuCategories } from "./models/categories";
import { tagManager } from "@/shared/api/newsquawk-headlines";
import {
  VirtuosoWrapper,
  GroupingUtils,
} from "@/ui-components/grouped-virtuoso";
import { DateSectionHeader } from "../firehose-widgets/shared/DateSectionHeader";
import { checkableTagIds } from "../create-headline/models/HeadlineForm";

/**
 * Everything the toggle hides.
 *
 *   195607 Research        — Newsquawk's own flagged research
 *   205460 Bank Research   — third-party notes, under Central Banks
 *   205442 Market Analysis — the [MARKET ANALYSIS] and daily open wraps
 *   195434 Highlighted     — the desk's longer highlighted pieces
 *
 * All four are long-form desk or third-party copy that mentions many
 * instruments in passing, so they answer a narrow search for any of them and
 * bury the headlines the filter was aimed at.
 *
 * Highlighted belongs here despite being an emphasis flag rather than a
 * content type: in practice the desk puts it on the same kind of long piece,
 * which is why it shares terminal-green with Research and why the two are
 * indistinguishable in the wire. Treating them differently was the thing that
 * made the toggle look broken.
 */
const RESEARCH_TAG_IDS = [
  checkableTagIds.Research,
  checkableTagIds.Highlighted,
  205460,
  205442,
];

const isResearch = (tagIds: number[] | undefined) =>
  !!tagIds?.some((id) => RESEARCH_TAG_IDS.includes(id));

/**
 * Tag-watchlist selections here are already raw tag ids — the curated menu tags
 * ride in as synthetic single-tag entries — so a tag found in the library needs
 * no new bucket and lands in the same one. Module-level to keep one identity.
 */
const TAG_LIBRARY = { menuId: "tag-watchlists-filter" } as const;

/**
 * Resolve tag IDs to display names.
 *
 * menuCategories covers the curated tags; anything picked out of the tag
 * library is named from the shared cache the search seeded, so the widget
 * title and the analytics read as tags rather than as bare ids.
 */
const allCategoryItems = menuCategories.flatMap((group) => group.items);
function resolveTagNames(tagIds: (string | number)[]): string {
  return tagIds
    .map(
      (id) =>
        allCategoryItems.find((item) => item.id === Number(id))?.title ??
        tagManager.getCached(Number(id))?.name
    )
    .filter(Boolean)
    .join(", ");
}

function NewsquawkHeadlines(props: CommonWidgetProps) {
  const { hasLiveAccess } = useAccessControl();
  const isPollingMode = useIsPollingMode();

  const {
    isLoading: headlinesLoading,
    headlines,
    loadNextPage,
    hasMore: hasMoreHeadlines,
  } = useHeadlines();

  const { deleteMutation } = useHeadlinesCRUD();

  // For users without live access (unauthenticated or non-subscriber), use delayed headlines
  const {
    headlines: delayedHeadlines,
    hasMore: delayedHasMore,
    loadNextPage: loadNextDelayedPage,
    isLoading: delayedIsLoading,
    loadingNextPage: delayedLoadingNextPage,
  } = useDelayedHeadlines({ subscribe: !hasLiveAccess });

  const {
    searchHeadlines,
    searchResults,
    clearSearchResults,
    isLoading: searchLoading,
    loadNext: loadNextSearchPage,
    hasMore: hasMoreSearchResults,
    isLoadingMore: isLoadingMoreSearchResults,
  } = useSearchHeadlines();

  // Keep track of the item being edited or deleted,
  // so that the relevant dialog can be shown
  const [editingItem, setEditingItem] = useState<Headline | null>(null);
  const [deletingItem, setDeletingItem] = useState<Headline | null>(null);


  const { portfolios } = usePortfoliosCRUD();

  const { queriesQuery: categoryQueries } = useHeadlineQueriesCRUD();

  // Get preview state from PortfolioBuilder context (if available)
  const portfolioBuilderContext = usePortfolioBuilderOptional();

  // Track which panel was last opened to coordinate closing
  const [lastOpenedPanel, setLastOpenedPanel] = useState<
    "filter" | "highlight" | null
  >(null);

  // Memoize onOpen callbacks for panel coordination
  const handleHighlightPanelOpen = useCallback(() => {
    setLastOpenedPanel("highlight");
  }, []);

  const handleFilterPanelOpen = useCallback(() => {
    setLastOpenedPanel("filter");
  }, []);

  // Highlight panel
  const {
    selections: highlightSelections,
    setSelections: setHighlightSelections,
    isPanelOpen: isHighlightPanelOpen,
    closePanel: closeHighlightPanel,
    filterMenus: highlightMenus,
    clearSelections: clearHighlightSelections,
    order: highlightOrder,
    moveInOrder: moveHighlight,
  } = useHeadlinesHighlightPanel({
    setActions: props.headerOptions?.setActions,
    pushAction: props.headerOptions?.pushAction,
    persistKey: props.widgetCompositeId,
    onOpen: handleHighlightPanelOpen,
    closeSearch: props.headerOptions?.closeSearch,
    onSearchOpen: props.headerOptions?.onSearchOpen,
  });

  // Filter panel
  const {
    selections: filterSelections,
    setSelections: setFilterSelections,
    isPanelOpen: isFilterPanelOpen,
    closePanel: closeFilterPanel,
    filterMenus,
    clearSelections: clearFilterSelections,
    filterMode,
    tagWatchlists,
  } = useHeadlinesFilterPanel({
    setActions: props.headerOptions?.setActions,
    pushAction: props.headerOptions?.pushAction,
    persistKey: props.widgetCompositeId,
    onOpen: handleFilterPanelOpen,
    closeSearch: props.headerOptions?.closeSearch,
    onSearchOpen: props.headerOptions?.onSearchOpen,
  });

  // Panel coordination: when one opens, close the other
  useEffect(() => {
    if (lastOpenedPanel === "filter" && isHighlightPanelOpen) {
      closeHighlightPanel();
    } else if (lastOpenedPanel === "highlight" && isFilterPanelOpen) {
      closeFilterPanel();
    }
  }, [
    lastOpenedPanel,
    isFilterPanelOpen,
    isHighlightPanelOpen,
    closeFilterPanel,
    closeHighlightPanel,
  ]);

  // Sync preview state from PortfolioBuilder to filter selections
  useEffect(() => {
    if (!portfolioBuilderContext) return;

    const { previewState } = portfolioBuilderContext;

    if (previewState.type === "watchlist" && previewState.id !== null) {
      // Preview a watchlist - set ticker-watchlists-filter (portfolios are ticker-based by default)
      setFilterSelections((prev) => ({
        ...prev,
        "ticker-watchlists-filter": [String(previewState.id)],
        "keyword-watchlists-filter": [],
        "category-filter": [],
        "basic-filter": [],
        "tag-watchlists-filter": [],
      }));
    } else if (previewState.type === "filter" && previewState.id !== null) {
      // Preview a custom filter - set category-filter
      setFilterSelections((prev) => ({
        ...prev,
        "category-filter": [String(previewState.id)],
        "ticker-watchlists-filter": [],
        "keyword-watchlists-filter": [],
        "basic-filter": [],
        "tag-watchlists-filter": [],
      }));
    } else if (previewState.type === "tagWatchlist" && previewState.tagIds) {
      // Preview a tag watchlist - set basic-filter with the tag IDs
      const tagIds = previewState.tagIds;
      setFilterSelections((prev) => ({
        ...prev,
        "basic-filter": tagIds,
        "ticker-watchlists-filter": [],
        "keyword-watchlists-filter": [],
        "category-filter": [],
        "tag-watchlists-filter": [],
      }));
    } else {
      // No preview - clear all preview-related filters
      setFilterSelections((prev) => ({
        ...prev,
        "ticker-watchlists-filter": [],
        "keyword-watchlists-filter": [],
        "category-filter": [],
        "basic-filter": [],
        "tag-watchlists-filter": [],
      }));
    }
  }, [portfolioBuilderContext?.previewState, setFilterSelections]);

  // Get global filter store actions for syncing portfolio selections (for sound alerts)
  // Identifies this widget instance in the shared alert store, so sibling
  // Headlines widgets don't overwrite (or on unmount erase) each other's
  // selections. Popouts/standalone renders have no composite id.
  const fallbackInstanceId = useId();
  const instanceKey = props.widgetCompositeId ?? fallbackInstanceId;

  const setPortfoliosFilter = useHeadlinesFilterStore(
    (state) => state.setPortfoliosFilter
  );
  const setPortfoliosHighlight = useHeadlinesFilterStore(
    (state) => state.setPortfoliosHighlight
  );

  const basicFilterSelected = useStableArray(
    filterSelections["basic-filter"] || []
  );

  // Get selected tag watchlists and extract their tag IDs
  const tagWatchlistsFilterSelected = useStableArray(
    filterSelections["tag-watchlists-filter"] || []
  );

  // Extract tag IDs from selected tag watchlists
  const tagWatchlistTagIds = useMemo(() => {
    if (tagWatchlistsFilterSelected.length === 0) return [];
    return tagWatchlistsFilterSelected.flatMap((watchlistId) => {
      const watchlist = tagWatchlists.find((w) => String(w.id) === watchlistId);
      if (watchlist) return watchlist.tags;
      /**
       * No watchlist owns this id, so it is a bare tag picked from the tag
       * library. The curated menu tags already arrive as synthetic single-tag
       * watchlists; this is the same idea for the tens of thousands that have
       * no entry to be synthesised from.
       */
      return /^\d+$/.test(watchlistId) ? [watchlistId] : [];
    });
  }, [tagWatchlistsFilterSelected, tagWatchlists]);

  // Get selected queries to search for
  const queriesToSearch = useStableArray(
    filterSelections["category-filter"]
      ?.map((id) => {
        if (categoryQueries.data) {
          return categoryQueries.data.find((query) => String(query.id) === id);
        }
        return undefined;
      })
      .filter(Boolean) || []
  ) as CategoryQuery[];

  // Extract tag IDs from advanced filters' embedded tag watchlists
  const advancedFilterTagIds = useMemo(() => {
    if (queriesToSearch.length === 0) return [];
    return queriesToSearch.flatMap((query) =>
      (query.tagsWatchlists || []).flatMap((tw) => tw.tags)
    );
  }, [queriesToSearch]);

  // Get selected portfolios from ticker and keyword watchlist filters
  const portfoliosFilterSelected = useStableArray([
    ...(filterSelections["ticker-watchlists-filter"] || []),
    ...(filterSelections["keyword-watchlists-filter"] || []),
  ]);

  // Sync portfolio filter/highlight selections to global store for sound alerts.
  // Includes portfolios embedded in active advanced filters (CategoryQueries)
  // so a portfolio with sound enabled fires even when it's only referenced via
  // a CategoryQuery rather than a standalone watchlist selection.
  useEffect(() => {
    const advancedFilterPortfolioIds = queriesToSearch.flatMap(
      (q) => (q.portfolios || []).map((p) => p.id)
    );

    const highlightPortfolioIds = [
      ...new Set([
        ...[
          ...(highlightSelections["ticker-watchlists-highlight"] ?? []),
          ...(highlightSelections["keyword-watchlists-highlight"] ?? []),
        ].map((id) => Number(id)),
        // Combined watchlists alert on their own settings too (negative ids).
        ...combinedAlertIds(highlightSelections["category-highlight"]),
      ]),
    ];
    const filterPortfolioIds = [
      ...new Set([
        ...[
          ...(filterSelections["ticker-watchlists-filter"] ?? []),
          ...(filterSelections["keyword-watchlists-filter"] ?? []),
        ].map((id) => Number(id)),
        ...advancedFilterPortfolioIds,
        ...combinedAlertIds(filterSelections["category-filter"]),
      ]),
    ];

    setPortfoliosHighlight(instanceKey, highlightPortfolioIds);
    setPortfoliosFilter(instanceKey, filterPortfolioIds);

    // Cleanup: clear selections when widget unmounts
    return () => {
      setPortfoliosHighlight(instanceKey, []);
      setPortfoliosFilter(instanceKey, []);
    };
  }, [
    filterSelections,
    highlightSelections,
    queriesToSearch,
    instanceKey,
    setPortfoliosFilter,
    setPortfoliosHighlight,
  ]);

  const hasFilters =
    queriesToSearch.length > 0 ||
    basicFilterSelected.length > 0 ||
    portfoliosFilterSelected.length > 0 ||
    tagWatchlistTagIds.length > 0;

  /**
   * Filtering routes through POST /headlines/search/full_text, which answers
   * 403 "Not authenticated". A logged-out visitor applying any filter
   * therefore got an empty widget reading "No items to display / End of
   * results" — not an empty result set, a rejected request.
   *
   * So search mode is for users who can actually use it, and the delayed feed
   * is filtered client-side below. It carries tag_id_list on every headline,
   * which is all a tag filter needs.
   */
  const canServerSearch = hasLiveAccess;
  const showSearchResults = canServerSearch && (hasFilters || searchLoading);

  // Determine which data source to use based on subscription status
  // Non-subscribers and unauthenticated users get delayed headlines, subscribers get live headlines
  const baseHeadlines = hasLiveAccess ? headlines : delayedHeadlines;
  const baseLoadNextPage = hasLiveAccess ? loadNextPage : loadNextDelayedPage;
  const baseHasMore = hasLiveAccess ? hasMoreHeadlines : delayedHasMore;
  const baseIsLoading = hasLiveAccess ? headlinesLoading : delayedIsLoading;
  const baseIsLoadingMore = hasLiveAccess
    ? headlinesLoading
    : delayedLoadingNextPage;

  // Determine which pagination/loading to use based on search state
  const loadNext = showSearchResults ? loadNextSearchPage : baseLoadNextPage;
  const hasMore = showSearchResults ? hasMoreSearchResults : baseHasMore;

  const isLoadingMore = showSearchResults
    ? isLoadingMoreSearchResults
    : baseIsLoadingMore;

  /**
   * Tag filtering for the delayed feed, done here because the search endpoint
   * is closed to this audience. Only tag filters are supported: portfolio and
   * tag-watchlist filters resolve against resources a logged-out visitor has
   * none of, so there is nothing to match them against.
   */
  const demoTagFilterIds = useMemo(
    () =>
      canServerSearch
        ? null
        : new Set(basicFilterSelected.map((id) => Number(id))),
    [canServerSearch, basicFilterSelected]
  );

  /* Show search results if there are any, else show recent headlines */
  // Filter headlines based on the portfolios to filter
  const filteredHeadlines = useMemo(() => {
    if (showSearchResults) return searchResults;
    if (!demoTagFilterIds?.size) return baseHeadlines;
    return baseHeadlines.filter((headline) =>
      (headline.tag_id_list ?? []).some((tagId) =>
        demoTagFilterIds.has(Number(tagId))
      )
    );
  }, [showSearchResults, searchResults, baseHeadlines, demoTagFilterIds]);

  // The widget's highlighted lists, in its order: the first a headline
  // matches paints it, and every match is named.
  const highlightCandidates = useMemo(
    () =>
      buildHighlightCandidates(highlightOrder, portfolios, categoryQueries.data),
    [highlightOrder, portfolios, categoryQueries.data]
  );

  const matchedData = applyFilters(filteredHeadlines, highlightCandidates);

  /**
   * Research is hidden from search and watchlist results by default.
   *
   * A research note is a long-form piece that matches broadly — mention a
   * ticker anywhere in it and it answers a search for that ticker — so on a
   * narrowed view they crowd out the desk copy the filter was aimed at. On the
   * unfiltered live wire they are ordinary headlines and stay put, which is why
   * this is gated on `showSearchResults` rather than applied everywhere.
   *
   * Opt-in rather than removed: the count says what is being withheld, so the
   * results are never quietly short of something the user asked for.
   */
  const [includeResearch, setIncludeResearch] = useState(false);

  const hiddenResearchCount = useMemo(
    () =>
      showSearchResults
        ? matchedData.filter(([headline]) => isResearch(headline.tag_id_list))
            .length
        : 0,
    [matchedData, showSearchResults]
  );

  const filteredData = useMemo(
    () =>
      showSearchResults && !includeResearch
        ? matchedData.filter(([headline]) => !isResearch(headline.tag_id_list))
        : matchedData,
    [matchedData, showSearchResults, includeResearch]
  );


  const handleQueriesApplied = useCallback(
    (queryIds: number[]) => {
      console.log("Queries applied: ", queryIds);

      setFilterSelections((prev) => ({
        ...prev,
        "category-filter": queryIds.map((id) => String(id)),
      }));
    },
    [setFilterSelections]
  );

  // Push polling mode indicator into widget header when in polling fallback
  useEffect(() => {
    if (isPollingMode && hasLiveAccess) {
      props.headerOptions?.pushAction(
        <PollingModeIndicator key="00-polling-mode" />
      );
    } else {
      props.headerOptions?.setActions((prev) =>
        prev.filter((a) => a.key !== "00-polling-mode")
      );
    }
  }, [isPollingMode, hasLiveAccess, props.headerOptions]);

  /**
   * Performs a search for headlines and updates the widget's title
   * whenever the selected filters change.
   *
   * The effect:
   * 1. Determines if any filters are active.
   * 2. If filters are active, constructs search parameters (tags, keywords, tickers)
   *    based on the selected filters and performs a search using `searchHeadlines`.
   * 3. Dynamically builds and sets the widget title to reflect the active filters.
   * 4. If no filters are active, resets the widget title to the default and clears search results.
   *
   */
  useEffect(() => {
    // Searching as a logged-out visitor is a guaranteed 403; the title still
    // needs setting from the filters, so only the fetch is skipped.
    const hasAdvancedFilters = queriesToSearch.length > 0;

    const hasFilters =
      hasAdvancedFilters ||
      basicFilterSelected.length > 0 ||
      portfoliosFilterSelected.length > 0 ||
      tagWatchlistTagIds.length > 0;

    if (hasFilters) {
      const portfolioFilterIds = portfoliosFilterSelected.map((id) =>
        String(id)
      );

      const portfoliosToSearch = mapPortfolioIdsToPortfolios(
        portfolios,
        portfolioFilterIds
      );

      // Combine basic filter tags, tag watchlist tags, and advanced filter tag watchlist tags (deduped)
      const allTagIds = [
        ...new Set([
          ...basicFilterSelected.map((id) => Number(id)),
          ...tagWatchlistTagIds.map((id) => Number(id)),
          ...advancedFilterTagIds.map((id) => Number(id)),
        ]),
      ];

      // Skipped for logged-out visitors: the endpoint answers 403 to them, so
      // this would be a request-per-keystroke that can only fail. Their
      // filtering happens client-side against the delayed feed instead.
      if (canServerSearch) {
        searchHeadlines({
        subscribed: true,
        exactMatch: false,
        /**
         * 100 rather than 50, because research is filtered out client-side
         * after the fetch — a research-heavy query for something like ECB can
         * lose a large part of a page to the toggle, leaving a filtered view
         * that looks emptier than the wire actually is.
         *
         * Unconditional rather than only while the toggle is off: making the
         * page size depend on it would change the search config, so ticking
         * the box would refetch and lose your place instead of just revealing
         * what is already loaded.
         *
         * The proper fix is `exclude_tags` on the search endpoint — the mirror
         * of the `tags` it already takes — which would make `limit` mean what
         * it says and stop fetching rows to discard. This is the version that
         * needs no backend change.
         */
        rows: 100,
        portfolios: portfoliosToSearch,
        advancedFilter: mergeCombinedFilters(queriesToSearch),
        tags: allTagIds,
        });
      }

      // Build title based on active filters
      // One flat list of what is actually applied. "Showing filtered results"
      // named the state without naming the cause, so a widget you came back to
      // could not tell you which watchlist was narrowing it.
      const titleParts: string[] = [];

      if (queriesToSearch.length > 0) {
        const filterNames = queriesToSearch.map((f) => f.queryName).filter(Boolean).map(formatListName).join(", ");
        if (filterNames) titleParts.push(filterNames);
      }
      if (portfoliosToSearch.length > 0) {
        const portfolioNames = portfoliosToSearch.map((p) => p.name).filter(Boolean).map(formatListName).join(", ");
        if (portfolioNames) titleParts.push(portfolioNames);
      }
      // Tag watchlists narrow the feed through allTagIds, so they were
      // filtering without ever naming themselves — the header said "Filtered"
      // while the panel showed the list right there in its Active row.
      if (tagWatchlistsFilterSelected.length > 0) {
        const tagWatchlistNames = tagWatchlistsFilterSelected
          .map((id) => tagWatchlists.find((w) => String(w.id) === id)?.name)
          .filter((name): name is string => !!name)
          .map(formatListName)
          .join(", ");
        if (tagWatchlistNames) titleParts.push(tagWatchlistNames);
      }
      if (basicFilterSelected.length > 0) {
        const assetClassNames = resolveTagNames(basicFilterSelected);
        if (assetClassNames) titleParts.push(assetClassNames);
      }

      if (titleParts.length > 0) {
        props.headerOptions?.setTitle(`Filtered: ${titleParts.join(", ")}`);
      } else {
        props.headerOptions?.setTitle("Filtered");
      }
    } else {
      props.headerOptions?.setTitle("Newsquawk Headlines");
      clearSearchResults();
    }
  }, [
    queriesToSearch,
    basicFilterSelected,
    portfoliosFilterSelected,
    tagWatchlistTagIds,
    // The title reads names off these, not just the tag ids derived from
    // them — without both, renaming a list leaves a stale header.
    tagWatchlistsFilterSelected,
    tagWatchlists,
    advancedFilterTagIds,
    props.headerOptions,
    clearSearchResults,
    searchHeadlines,
    canServerSearch,
    portfolios,
    filterMode,
  ]);

  // ── Track filter usage (debounced) ──────────────────────────────────
  const filterTrackingTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (filterTrackingTimer.current) clearTimeout(filterTrackingTimer.current);
    filterTrackingTimer.current = setTimeout(() => {
      if (basicFilterSelected.length > 0) {
        trackFilterApplied({
          filter_context: "headlines",
          filter_type: "category",
          filter_value: resolveTagNames(basicFilterSelected) || basicFilterSelected.join(", "),
          filter_count: basicFilterSelected.length,
        });
      }
      if (portfoliosFilterSelected.length > 0) {
        const portfolioNames = portfoliosFilterSelected
          .map((id) => portfolios?.find((p) => String(p.id) === String(id))?.name)
          .filter(Boolean)
          .join(", ");
        trackFilterApplied({
          filter_context: "headlines",
          filter_type: "portfolio",
          filter_value: portfolioNames || portfoliosFilterSelected.join(", "),
          filter_count: portfoliosFilterSelected.length,
        });
      }
      if (tagWatchlistTagIds.length > 0) {
        trackFilterApplied({
          filter_context: "headlines",
          filter_type: "tag_watchlist",
          filter_value: resolveTagNames(tagWatchlistTagIds) || "tag_watchlist",
          filter_count: tagWatchlistTagIds.length,
        });
      }
      if (queriesToSearch.length > 0) {
        trackFilterApplied({
          filter_context: "headlines",
          filter_type: "advanced_filter",
          filter_value: queriesToSearch.map((q) => q.queryName).join(", "),
          filter_count: queriesToSearch.length,
        });
      }
    }, 500);
    return () => {
      if (filterTrackingTimer.current) clearTimeout(filterTrackingTimer.current);
    };
  }, [basicFilterSelected, portfoliosFilterSelected, tagWatchlistTagIds, queriesToSearch, portfolios]);

  return (
    <>
      <div className="h-full flex flex-col relative">
        {/* Filter Panel - inline above content */}
        <FilterHighlightPanels
          filterMenus={filterMenus}
          filterSelections={filterSelections}
          setFilterSelections={setFilterSelections}
          isFilterOpen={isFilterPanelOpen}
          onClearFilter={clearFilterSelections}
          onCloseFilter={closeFilterPanel}
          highlightMenus={highlightMenus}
          highlightSelections={highlightSelections}
          setHighlightSelections={setHighlightSelections}
          isHighlightOpen={isHighlightPanelOpen}
          onClearHighlight={clearHighlightSelections}
          onCloseHighlight={closeHighlightPanel}
          highlightOrder={highlightOrder}
          onMoveHighlight={moveHighlight}
          tagLibrary={TAG_LIBRARY}
        />

        {/* Only when something is actually being withheld — a checkbox
            offering to include nothing is noise. */}
        {showSearchResults && (hiddenResearchCount > 0 || includeResearch) && (
          <label className="flex cursor-pointer items-center gap-2 border-b border-gray-200 bg-gray-100/90 px-3 py-1.5 text-xs text-gray-500 dark:border-gray-700 dark:bg-gray-800/80 dark:text-gray-400">
            <input
              type="checkbox"
              checked={includeResearch}
              onChange={(e) => setIncludeResearch(e.target.checked)}
              className="size-3.5 cursor-pointer rounded border-gray-600 bg-gray-800 text-squawk-blue focus:ring-0 focus:ring-offset-0"
            />
            <span>Include Newsquawk research in results</span>
            {!includeResearch && hiddenResearchCount > 0 && (
              <span className="font-mono tabular-nums text-gray-400 dark:text-gray-500">
                {hiddenResearchCount} hidden
              </span>
            )}
          </label>
        )}

        {/** Headlines list */}
        <VirtuosoWrapper
          data={filteredData}
          grouping={{
            groupBy: (items) =>
              GroupingUtils.groupByDate(items, (item) => {
                const h = item[0];
                return typeof h.published_at === "object"
                  ? new Date(h.published_at.unix * 1000).toISOString()
                  : (h.published_at as string);
              }, {
                includeYesterday: true,
                dateFormat: { weekday: "short", month: "long", day: "numeric", year: "numeric" },
              }),
            // "Today" is the default state of a live feed, so labelling it spends a row
            // saying nothing — and a filtered view opening on a date header reads
            // as an archive rather than something still arriving. Later dates keep
            // their separators; only the first group is suppressed.
            hideFirstGroupHeader: true,
          }}
          scrollManager={{
            enabled: true,
            compareFirstItem: (a, b) => a?.[0]?.id === b?.[0]?.id,
          }}
          pagination={{
            hasMore,
            isLoadingMore,
            loadNext,
          }}
          renderItem={([headline, highlightInfo]) => (
            <NewsquawkHeadlineItem
              headline={headline}
              key={headline.id}
              highlightInfo={highlightInfo}
              onItemDelete={setDeletingItem}
              onItemEdit={setEditingItem}
            />
          )}
          renderGroupHeader={(title) => <DateSectionHeader title={title} />}
          renderEmpty={() => (
            <EmptyPlaceholder
              isLoading={showSearchResults ? searchLoading : baseIsLoading}
              delay={200}
            />
          )}
          className="flex-1 scrollbar-thin bg-white dark:bg-gray-900 relative"
        />
      </div>

      {/* Dialogs */}
      <section>
        <DeleteConfirmationDialog
          isOpen={deletingItem !== null}
          onClose={() => setDeletingItem(null)}
          onConfirmDelete={
            deletingItem
              ? () => deleteMutation.mutateAsync(deletingItem.id)
              : undefined
          }
        />

        <EditHeadlineFormDialog
          isOpen={editingItem !== null}
          headline={editingItem}
          onClose={() => setEditingItem(null)}
        />
      </section>
    </>
  );
}

export default NewsquawkHeadlines;
